import { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useCurrentUser, useLogout, useFetchTemplates } from "utils/hooks";
import {
  addPrivateTemplateAction,
  addPublicTemplateAction,
} from "store/actions/templates";

const useCopyTemplate = () => {
  const { user } = useCurrentUser();
  const { logOut } = useLogout();
  const { t } = useTranslation("common");
  const { fetchPrivateTemplates, fetchPublicTemplates } = useFetchTemplates();
  const dispatch = useDispatch();
  const [copying, setCopying] = useState(false);

  const copyTemplate = async (templateId: string) => {
    if (!templateId) return null;

    setCopying(true);

    return new Promise(() => {
      fetch(`${process.env.REACT_APP_API_URL}`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          query: `
              mutation {
                copyTemplateAsNewDraft(templateId: "${templateId}") {
                  uuid
                  title
                  description
                  instructions
                  authorId
                  slug
                  order
                  content
                  status
                  type
                  category
                  default
                  icon
                  color
                  assessmentScoring {
                    isScoringEnabled
                    scoringInstructions
                    scoringType
                    scoreSuffix
                    scoreAsPercentage
                    isFieldsetScoringEnabled
                    fieldSets {
                      id
                      name
                      fieldScoringType
                      order
                    }
                    scoreDisplays {
                      id
                      name
                      min
                      max
                      color
                      order
                    }
                  }
                }
              }
            `,
        }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId: user?.activeOrganizationId || "NA",
          permissionKey: "copy_template",
        },
      })
        .then(async (res) => {
          if (res.status !== 200 && res.status !== 201) {
            const error = await res.json();
            if (error.errors[0].message.includes("BRKN_6001")) {
              toast.warn(t("errors.login_again_error"));
              logOut();
            }
            if (
              error.errors[0].message.includes(
                "CUSTOM_ASSIGNMENT_LIMIT_REACHED"
              )
            ) {
              toast.warn(t("errors.assignment_limit_error"));
            } else {
              toast.error(
                `${error.errors[0].message || t("errors.something_went_wrong")}`
              );
              throw new Error("Failed!");
            }
          }
          return res.json();
        })
        .then((resData) => {
          if (resData.errors) {
            toast.error(
              `${t("errors.something_went_wrong")}. ${
                resData.errors[0].message
              }`
            );
            return true;
          } else {
            toast("Your assignment template draft has been created");
            // ReactGA.event("custom_assignment_created_success", {
            //   user_id: user?.id,
            //   type: resData.data.createTemplate.type,
            // });
            const isPublic =
              resData.data.copyTemplateAsNewDraft.authorId ===
              process.env.REACT_APP_TEMPLATE_MASTER_ID;
            if (isPublic) {
              dispatch(
                addPublicTemplateAction(resData.data.copyTemplateAsNewDraft)
              );
              fetchPublicTemplates();
            } else {
              dispatch(
                addPrivateTemplateAction(resData.data.copyTemplateAsNewDraft)
              );
              fetchPrivateTemplates();
            }
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setCopying(false);
          return true;
        });
    });
  };

  return {
    copyTemplate,
    copying,
  };
};

export default useCopyTemplate;
